import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {requestStores} from '../../../ListOfPREditors.brick.jsx';
import {PopupField} from './PopupField.jsx';
import {PartnerFunctionPopup} from './PartnerFunctionPopup.jsx';
import {invoicePartnerOrderingAddressLambda} from '../../../../utils/functionSignatures.js';
import {LAMBDA_NAMES, mandatoryFieldsForRequester, mandatoryFieldsForSpender} from '../../../../utils/constants';
import {BrickContext} from 'olympe';

/**
 * Field to edit the Ordering Address or Invoice Partner, if there are multiple options available.
 * Opens a popup to select the value.
 *
 * @param {PartnerFunctionField.propTypes} props
 * @return {JSX.Element}
 */
export function PartnerFunctionField(props) {
    // state
    const useRequestStore = requestStores.get(props.prTag);

    const [orderingAddress, setOrderingAddress] = useRequestStore((state) => [state.orderingAddress, state.setOrderingAddress]);
    const [invoicePartner, setInvoicePartner] = useRequestStore((state) => [state.invoicePartner, state.setInvoicePartner]);
    const setAreThereMultipleOrderingAddresses = useRequestStore((state) => state.setAreThereMultipleOrderingAddresses);
    const setAreThereMultipleInvoicePartners = useRequestStore((state) => state.setAreThereMultipleInvoicePartners);
    const [highlightMandatoryFieldForRequester, highlightMandatoryFieldForSpender] = useRequestStore((state) => [state.highlightMandatoryFieldForRequester, state.highlightMandatoryFieldForSpender]);

    const vendorCode = useRequestStore((state) => state.vendorCode);
    const purchaseOrganization = useRequestStore((state) => state.purchaseOrganization);
    const plant = useRequestStore((state) => state.defaultPlant);

    const [options, setOptions] = useState([]);
    const [readOnly, setReadOnly] = useState(true);
    const [loading, setLoading] = useState(false);
    const [openPopup, setOpenPopup] = useState(false);
    const [defaultValue, setDefaultValue] = useState('');

    // interactions
    const handleSelectOption = (selectedEntry) => {
        setOpenPopup(false);
        const newValue = {
            label: `${selectedEntry['lifn2']} - ${selectedEntry['name1']} - ${selectedEntry['ort01']}`,
            value: selectedEntry['lifn2'],
        };
        if (props.partnerFunction === 'PI') {
            setInvoicePartner(newValue);
        } else {
            setOrderingAddress(newValue);
        }
    };

    const handleClear = () => {
        const newValue = {label: '', value: defaultValue};
        if (props.partnerFunction === 'PI') {
            setInvoicePartner(newValue);
        } else {
            setOrderingAddress(newValue);
        }
    };

    // Fetch the options, and display the field according if there are multiple results or not
    const getPartnerBrick = props.context.get(LAMBDA_NAMES.GET_PARTNER_FUNCTION);
    useEffect(() => {
        // To avoid an error 'Can't perform a React state update on an unmounted component'.
        // Reproduce it -> if we expand the item and then immediately collapse it, getting this error
        let cleanupFunction = false;
        setLoading(true);
        let partnerFunction;

        // Since we use the Olympe Gateway, the data are coming from the AMDR DB and the acronyms of partner functions are different
        if (props.partnerFunction === 'OA') {
            partnerFunction = 'BA';
        } else if (props.partnerFunction === 'PI') {
            partnerFunction = 'RS';
        }

        invoicePartnerOrderingAddressLambda(
            props.context,
            getPartnerBrick,
            vendorCode,
            purchaseOrganization,
            partnerFunction,
            plant,
            (resultRunner) => {
                if (!cleanupFunction) {
                    const result = JSON.parse(resultRunner);

                    // Remove the duplicates
                    const resultWithoutDuplicates = result.filter((value, index, self) => {
                        return index === self.findIndex((t) => {
                            return t['lifn2'] === value['lifn2'] && t['name1'] === value['name1'] && t['ort01'] === value['ort01'];
                        }, index + 1);
                    });

                    if (props.partnerFunction === 'PI') {
                        setAreThereMultipleInvoicePartners(resultWithoutDuplicates.length > 1);
                    } else {
                        setAreThereMultipleOrderingAddresses(resultWithoutDuplicates.length > 1);
                    }

                    if (resultWithoutDuplicates.length > 1) {
                        setOptions(resultWithoutDuplicates);

                        const joinedValues = resultWithoutDuplicates.map((pf) => pf.lifn2).join(';');
                        const object = {
                            label: '',
                            value: joinedValues,
                        };

                        // If the user doesn't choose anything, and we have multiple possibilities, set all the possibilities
                        if (props.partnerFunction === 'PI' && (!invoicePartner.value || invoicePartner.value && invoicePartner.value !== '' && invoicePartner.value === invoicePartner.label && joinedValues.includes(invoicePartner.value))) {
                            setInvoicePartner(object);
                        } else if (props.partnerFunction === 'OA' && (!orderingAddress.value || orderingAddress.value && orderingAddress.value !== '' && orderingAddress.value === orderingAddress.label && joinedValues.includes(orderingAddress.value))) {
                            setOrderingAddress(object);
                        }

                        setReadOnly(false);
                        setDefaultValue(joinedValues);
                    } else if (resultWithoutDuplicates.length === 1) {
                        setOptions([]);
                        const object = {
                            label: `${resultWithoutDuplicates[0].lifn2} - ${resultWithoutDuplicates[0].name1} - ${resultWithoutDuplicates[0].ort01}`,
                            value: '', // If there is only one choice, we do not send it to SAP, so we don't store the value
                        };
                        props.partnerFunction === 'PI' ?
                            setInvoicePartner(object) :
                            setOrderingAddress(object);
                        setReadOnly(true);
                    } else {
                        setOptions([]);
                        props.partnerFunction === 'PI' ? setInvoicePartner({
                            label: '',
                            value: '',
                        }) : setOrderingAddress({label: '', value: ''});
                        setReadOnly(true);
                    }
                    setLoading(false);
                }
            },
            () => setLoading(false),
        ).then(() => {});
        return () => cleanupFunction = true;
    }, [props.partnerFunction, vendorCode, purchaseOrganization, plant]);

    let showMultiple = false;

    if (options.length > 1) {
        const pf = props.partnerFunction === 'PI' ? invoicePartner : orderingAddress;
        showMultiple = pf.value.includes(';');
    }

    return (<>
        <PopupField
            value={props.partnerFunction === 'PI' ? invoicePartner.label : orderingAddress.label}
            showMultiple={showMultiple}
            placeholder={options.length > 1 ? 'Multiple' : ''}
            optionsLength={options.length}
            canEdit={!props.disabled || !readOnly}
            loading={loading}
            onOpen={() => setOpenPopup(true) }
            onClear={handleClear}
            property={'Partner functions'}
            highlightMandatoryFieldForRequester={highlightMandatoryFieldForRequester}
            highlightMandatoryFieldForSpender={highlightMandatoryFieldForSpender}
            mandatoryForSpender={showMultiple && mandatoryFieldsForSpender.includes(props.partnerFunction === 'PI' ? 'Invoice partner' : 'Ordering address')}
            mandatoryForRequester={showMultiple && mandatoryFieldsForRequester.includes(props.partnerFunction === 'PI' ? 'Invoice partner' : 'Ordering address')}
        />
        <PartnerFunctionPopup
            open={openPopup}
            partnerFunction={props.partnerFunction}
            options={options}
            onSelectOption={handleSelectOption}
            onClose={ () => setOpenPopup(false) }
        />
    </>);
}

PartnerFunctionField.propTypes = {
    prTag: PropTypes.string,
    partnerFunction: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    disabled: PropTypes.bool,
};
